import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import SearchIcon from '@mui/icons-material/Search'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import {
  format,
  money,
  dateHeureLisible,
  estEngagement,
  libelleStatut,
  referenceAffichable,
  soldeDocument,
  type CurrencyCode,
  type Transaction,
} from '@iobiz/shared'
import {
  useItem,
  useReferencesDocuments,
  useRendezVousParVente,
  useTotauxDuJour,
  useVentes,
} from '../data/hooks.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { DataTable, EmptyState, type Column } from '../ui/DataTable.js'
import type { RequeteVentes } from '../data/repositories/sales.local.js'

/**
 * Journal des ventes.
 *
 * ⭐ Deux indicateurs seulement en tête : le nombre de ventes du jour et ce
 * qu'elles ont rapporté. C'est ce qu'un commerçant regarde en premier, et le
 * reste tient dans le tableau.
 */

/** Libellés des types de document, les mêmes que sur la fiche d'un document. */
const TYPES_DOC: Record<string, string> = {
  quote: 'Devis',
  invoice: 'Vente',
  deposit_invoice: 'Acompte',
  credit_note: 'Avoir',
  order: 'Commande',
}

/**
 * Couleur du statut. Le TEXTE vient du paquet partagé : « validé » se dit
 * « À régler » sur une facture et « Émis » sur un devis, où rien n'est à régler.
 */
const COULEURS: Record<
  Transaction['status'],
  'default' | 'info' | 'warning' | 'success' | 'error'
> = {
  draft: 'default',
  validated: 'info',
  partially_paid: 'warning',
  paid: 'success',
  cancelled: 'error',
}

export function SalesListPage(): JSX.Element {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => s.stores.find((b) => b.slug === slug))
  const devise = (store?.currencyCode ?? 'XAF') as CurrencyCode

  const jour = new Date().toISOString().slice(0, 10)

  // ⭐ Les filtres vivent dans l'URL, comme au catalogue et aux clients (§7.9) :
  // la vue est partageable, rechargeable et navigable au bouton Précédent.
  const [params, setParams] = useSearchParams()
  const requete: RequeteVentes = useMemo(
    () => ({
      limite: 200,
      q: params.get('q') ?? undefined,
      docType: (params.get('type') as Transaction['docType']) ?? undefined,
      statut: (params.get('statut') as Transaction['status']) ?? undefined,
      // ⭐ Posé par le clic sur un stock négatif au catalogue : « quelles
      // commandes expliquent ce trou ? ».
      itemId: params.get('item') ?? undefined,
      sort: params.get('sort') ?? '-date',
    }),
    [params],
  )

  const ventes = useVentes(store?.storeId, requete)
  const totaux = useTotauxDuJour(store?.storeId, jour)
  const references = useReferencesDocuments(store?.storeId)
  const rdvParVente = useRendezVousParVente(store?.storeId)
  // Le nom de l'article filtré, pour dire CE QU'ON regarde, pas seulement
  // qu'un filtre est actif.
  const articleFiltre = useItem(store?.storeId, requete.itemId)

  const [selection, setSelection] = useState<string[]>([])
  // Champ découplé de l'URL : on n'écrit l'adresse qu'après l'anti-rebond.
  const [saisie, setSaisie] = useState(requete.q ?? '')

  const patch = useCallback(
    (changes: Record<string, string | undefined>) => {
      const suite = new URLSearchParams(params)
      for (const [clef, valeur] of Object.entries(changes)) {
        if (valeur === undefined || valeur === '') suite.delete(clef)
        else suite.set(clef, valeur)
      }
      setParams(suite, { replace: true })
    },
    [params, setParams],
  )

  /**
   * ⚠️ Anti-rebond COURT — 150 ms, contre 300 ailleurs. Une douchette « tape »
   * treize chiffres en quelques millisecondes puis envoie Entrée : un délai
   * long laisserait le caissier devant une liste qui n'a pas encore bougé.
   */
  useEffect(() => {
    const minuteur = setTimeout(() => {
      if (saisie !== (requete.q ?? '')) patch({ q: saisie })
    }, 150)
    return () => clearTimeout(minuteur)
  }, [saisie, requete.q, patch])

  const filtresActifs = Boolean(
    requete.q || requete.docType || requete.statut || requete.itemId,
  )

  const prix = (v: number): string => format(money(v, devise), 'fr-FR')

  const colonnes: Column<Transaction>[] = [
    {
      key: 'reference',
      label: 'Référence',
      sortKey: 'reference',
      render: (v) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {referenceAffichable(v)}
            </Typography>
            {/* ⭐ D'OÙ VIENT ce document. Une facture née d'un devis accepté
                portait l'identifiant de ce devis sans jamais le montrer :
                impossible, depuis le journal, de savoir qu'une vente
                répondait à un chiffrage — ni de remonter au prix promis. */}
            {v.parentId && references.get(v.parentId) && (
              <Typography
                variant="caption"
                sx={{
                  color: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  navigate(`/s/${slug}/ventes/${v.parentId}`)
                }}
              >
                {references.get(v.parentId)!.docType === 'quote' ? 'issue du devis ' : 'suite de '}
                {references.get(v.parentId)!.reference}
              </Typography>
            )}
            {/* ⭐ Le RENDEZ-VOUS attaché, s'il existe — DANS le même bloc que
                « issue du devis », donc sous la référence et non à côté. Placé
                en frère de ce bloc, il se retrouvait sur la même ligne que le
                numéro, aligné avec lui : la mention se lisait comme la suite du
                numéro, pas comme une information distincte. */}
            {rdvParVente?.get(v.id) && (
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={(e) => {
                  e.stopPropagation()
                  const rdv = rdvParVente.get(v.id)!
                  navigate(`/s/${slug}/agenda?jour=${rdv.startsAt.slice(0, 10)}`)
                }}
              >
                Rendez-vous fixé le{' '}
                {new Date(rdvParVente.get(v.id)!.startsAt).toLocaleString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
            )}
          </Box>
          {/* ⚠️ Un numéro provisoire doit se voir : c'est la seule chose qui
              distingue une vente déjà connue du serveur d'une vente qui n'a
              pas encore quitté cet appareil. */}
          {!v.number && (
            <Chip
              icon={<CloudOffIcon />}
              label="provisoire"
              size="small"
              variant="outlined"
              color="warning"
            />
          )}
        </Box>
      ),
    },
    {
      /**
       * ⚠️ Le journal montre AUSSI les devis. Sans cette colonne, un devis de
       * 226 000 F s'y alignait comme une vente « Brouillon » — impossible de
       * distinguer une proposition de prix d'un encaissement en attente.
       */
      key: 'type',
      label: 'Type',
      sortKey: 'type',
      render: (v) => (
        <Chip
          size="small"
          variant="outlined"
          label={TYPES_DOC[v.docType] ?? v.docType}
          color={v.docType === 'quote' ? 'info' : 'default'}
        />
      ),
    },
    {
      key: 'date',
      label: 'Date et heure',
      sortKey: 'date',
      render: (v) => dateHeureLisible(v.createdAt),
    },
    {
      key: 'client',
      label: 'Client',
      sortKey: 'client',
      render: (v) => v.customerName ?? 'Comptoir',
    },
    {
      key: 'caissier',
      sortKey: 'acteur',
      // ⚠️ Le journal liste aussi les devis, qui ne s'encaissent pas.
      // « Établi par » dit vrai des deux, sans rien perdre : c'est la première
      // question posée devant un écart de caisse, et un identifiant technique
      // n'y répondrait pas.
      label: 'Établi par',
      render: (v) => v.cashierName ?? '—',
    },
    {
      key: 'statut',
      label: 'Statut',
      sortKey: 'statut',
      render: (v) => (
        <Chip
          label={libelleStatut(v.docType, v.status)}
          color={COULEURS[v.status]}
          size="small"
        />
      ),
    },
    {
      key: 'reste',
      label: 'Reste dû',
      sortKey: 'reste',
      align: 'right',
      numeric: true,
      /**
       * ⭐ SIGNÉ, et coloré dans les deux sens. La colonne ne montrait que ce
       * que le client devait : quand la boutique encaisse un billet sans
       * pouvoir rendre la monnaie, elle reste devoir la différence, et ce
       * cas-là n'apparaissait nulle part — il se réglait de mémoire.
       *
       * ⚠️ Rien n'est dû sur un devis ou une commande : le total entier s'y
       * affichait en créance.
       */
      render: (v) => {
        if (estEngagement(v.docType)) return '—'
        const solde = soldeDocument(v)
        if (solde === 0) return '—'
        const boutiqueDoit = solde < 0
        return (
          <Tooltip
            title={
              boutiqueDoit
                ? 'La boutique doit cette somme au client'
                : 'Le client doit cette somme à la boutique'
            }
          >
            <Typography
              variant="body2"
              className="tabular"
              sx={{ fontWeight: 600, color: boutiqueDoit ? 'error.main' : 'warning.main' }}
            >
              {boutiqueDoit ? '− ' : '+ '}
              {prix(Math.abs(solde))}
            </Typography>
          </Tooltip>
        )
      },
    },
    {
      key: 'total',
      label: 'Total TTC',
      sortKey: 'total',
      align: 'right',
      numeric: true,
      render: (v) => (
        <Typography variant="body2" className="tabular" sx={{ fontWeight: 600 }}>
          {prix(v.totalAmount)}
        </Typography>
      ),
    },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 5 }}>
        <Box>
          <Typography variant="h1">Ventes</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            {ventes?.length ?? 0} document{(ventes?.length ?? 0) > 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {/* ⭐ N'apparaît QUE sur sélection, comme ailleurs dans l'application :
              une action permanente invite à agir sur tout le fichier par mégarde. */}
          {selection.length > 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {selection.length} sélectionnée{selection.length > 1 ? 's' : ''}
            </Typography>
          )}
        <Button
          variant="contained"
          size="large"
          startIcon={<PointOfSaleIcon />}
          onClick={() => navigate(`/s/${slug}/caisse`)}
        >
          Nouvelle vente
        </Button>
        </Box>
      </Box>

      {/* ⭐ D'où vient ce filtre. On arrive ici depuis le stock négatif d'un
          article au catalogue : sans ce bandeau, la liste semble filtrée par
          hasard, et rien ne dit sur quel article ni comment revenir en
          arrière autrement que par « Réinitialiser ». */}
      {requete.itemId && (
        <Alert severity="info" sx={{ mb: 4 }} onClose={() => patch({ item: undefined })}>
          Commandes en attente de stock pour{' '}
          <strong>{articleFiltre?.name ?? 'cet article'}</strong>.
        </Alert>
      )}

      {/* ⭐ Le champ accepte aussi un SCAN.
          Le ticket porte un EAN-13 ; la douchette le tape ici comme un clavier,
          et la recherche le retraduit en numéro de document. C'est le geste
          qu'on veut au comptoir quand un client revient avec son ticket. */}
      <Paper sx={{ p: 4, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
            placeholder="Scanner un ticket, ou chercher un numéro, un client…"
            sx={{ flex: 1, minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Type"
            size="small"
            value={requete.docType ?? ''}
            onChange={(e) => patch({ type: e.target.value })}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">Tous</MenuItem>
            {Object.entries(TYPES_DOC).map(([code, libelle]) => (
              <MenuItem key={code} value={code}>
                {libelle}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Statut"
            size="small"
            value={requete.statut ?? ''}
            onChange={(e) => patch({ statut: e.target.value })}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="">Tous</MenuItem>
            {/* ⚠️ Les libellés sont ceux d'une VENTE : ce filtre porte sur le
                statut brut, commun aux deux, et « validé » se dit autrement sur
                un devis. Le rappel entre parenthèses évite de chercher
                « Émis » dans une liste qui ne le propose pas. */}
            <MenuItem value="draft">Brouillon</MenuItem>
            <MenuItem value="validated">À régler (devis : émis)</MenuItem>
            <MenuItem value="partially_paid">Partiel</MenuItem>
            <MenuItem value="paid">Réglée</MenuItem>
            <MenuItem value="cancelled">Annulée (devis : refusé)</MenuItem>
          </TextField>
          {filtresActifs && (
            <Button
              onClick={() => {
                setSaisie('')
                setParams({}, { replace: true })
              }}
            >
              Réinitialiser
            </Button>
          )}
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 4,
          mb: 5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 16rem))' },
        }}
      >
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Ventes du jour
          </Typography>
          <Typography variant="h2" className="tabular" sx={{ fontWeight: 800, mt: 1 }}>
            {totaux.ventes}
          </Typography>
        </Paper>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Encaissé aujourd’hui
          </Typography>
          <Typography variant="h2" className="tabular" sx={{ fontWeight: 800, mt: 1 }}>
            {prix(totaux.encaisse)}
          </Typography>
        </Paper>
      </Box>

      {ventes && ventes.length === 0 ? (
        <EmptyState
          icon={<PointOfSaleIcon />}
          title={filtresActifs ? 'Aucun résultat' : 'Aucune vente'}
          description={
            filtresActifs
              ? 'Aucun document ne correspond aux critères retenus, sur cet appareil.'
              : 'Enregistrez votre première vente au comptoir.'
          }
          action={
            filtresActifs ? (
              <Button
                onClick={() => {
                  setSaisie('')
                  setParams({}, { replace: true })
                }}
              >
                Réinitialiser les filtres
              </Button>
            ) : (
              <Button variant="contained" onClick={() => navigate(`/s/${slug}/caisse`)}>
                Nouvelle vente
              </Button>
            )
          }
        />
      ) : (
        <DataTable
          rows={ventes ?? []}
          columns={colonnes}
          rowKey={(v) => v.id}
          total={ventes?.length ?? 0}
          sort={requete.sort}
          onSortChange={(sort) => patch({ sort })}
          selected={selection}
          onSelectionChange={setSelection}
          onRowClick={(v) => navigate(`/s/${slug}/ventes/${v.id}`)}
        />
      )}
    </Box>
  )
}
